import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { ArrowLeft, FileText, Loader2 } from "lucide-react";
import { ProtocolPhotoGallery } from "@/components/equipment/ProtocolPhotoGallery";
import {
  protocolDirectionLabel,
  protocolItemMeta,
  protocolStatusLabel,
} from "@/components/equipment/equipmentLabels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRole } from "@/contexts/RoleContext";
import { useEquipmentProtocolHistory } from "@/hooks/useEquipmentProtocolHistory";
import type { EquipmentProtocol } from "@/types/equipment";

type HistoryFilter = "all" | "accepted" | "rejected";

export default function EquipmentProtocolHistoryPage() {
  const navigate = useNavigate();
  const { currentOrgId } = useRole();
  const { data, isPending, isError } = useEquipmentProtocolHistory(currentOrgId);
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const [selected, setSelected] = useState<EquipmentProtocol | null>(null);

  const protocols = useMemo(() => {
    const rows = data?.protocols ?? [];
    if (filter === "all") return rows;
    return rows.filter((item) => item.status === filter);
  }, [data?.protocols, filter]);

  const selectedMeta = selected
    ? protocolItemMeta(selected, data?.assets ?? [], data?.keys ?? [])
    : null;

  if (!currentOrgId) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-muted-foreground">
        Brak wybranej organizacji.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} title="Powrót do pulpitu">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <FileText className="h-6 w-6" />
            Historia protokołów
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Zaakceptowane i odrzucone protokoły przekazania oraz zwrotu sprzętu
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Protokoły sprzętu</CardTitle>
            <CardDescription>Najnowsze wpisy są na górze listy.</CardDescription>
          </div>
          <Tabs value={filter} onValueChange={(value) => setFilter(value as HistoryFilter)}>
            <TabsList>
              <TabsTrigger value="all">Wszystkie</TabsTrigger>
              <TabsTrigger value="accepted">Zaakceptowane</TabsTrigger>
              <TabsTrigger value="rejected">Odrzucone</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Ładowanie historii...
            </div>
          ) : isError ? (
            <p className="py-12 text-center text-sm text-destructive">
              Nie udało się pobrać historii protokołów.
            </p>
          ) : protocols.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Brak protokołów w wybranym filtrze.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Czynność</TableHead>
                  <TableHead>Przedmiot</TableHead>
                  <TableHead>Pracownik</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Szczegóły</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {protocols.map((protocol) => {
                  const meta = protocolItemMeta(protocol, data?.assets ?? [], data?.keys ?? []);
                  const dateValue = protocol.responded_at ?? protocol.initiated_at;
                  return (
                    <TableRow key={protocol.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(dateValue), "dd.MM.yyyy HH:mm", { locale: pl })}
                      </TableCell>
                      <TableCell>{protocolDirectionLabel(protocol.direction)}</TableCell>
                      <TableCell>
                        <p className="font-medium">{meta.name}</p>
                        {meta.serialNumber && (
                          <p className="text-xs text-muted-foreground">{meta.serialNumber}</p>
                        )}
                        {protocol.status === "rejected" && protocol.rejection_reason && (
                          <p className="mt-1 text-xs text-destructive">
                            Powód: {protocol.rejection_reason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>{data?.names[protocol.worker_id] ?? "Pracownik"}</TableCell>
                      <TableCell>
                        <Badge variant={protocol.status === "rejected" ? "destructive" : "secondary"}>
                          {protocolStatusLabel(protocol.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => setSelected(protocol)}>
                          Podgląd
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selected ? protocolDirectionLabel(selected.direction) : "Protokół"}
            </DialogTitle>
            <DialogDescription>
              {selected ? protocolStatusLabel(selected.status) : "Szczegóły protokołu sprzętu"}
            </DialogDescription>
          </DialogHeader>
          {selected && selectedMeta && (
            <div className="space-y-4 py-2 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Przedmiot</p>
                <p className="font-medium">
                  {selectedMeta.name}
                  {selectedMeta.serialNumber ? ` (${selectedMeta.serialNumber})` : ""}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pracownik</p>
                <p className="font-medium">{data?.names[selected.worker_id] ?? "Pracownik"}</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">Wszczęto</p>
                  <p className="font-medium">
                    {format(new Date(selected.initiated_at), "dd.MM.yyyy HH:mm", { locale: pl })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rozstrzygnięto</p>
                  <p className="font-medium">
                    {selected.responded_at
                      ? format(new Date(selected.responded_at), "dd.MM.yyyy HH:mm", { locale: pl })
                      : "—"}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Stan wpisany przy protokole</p>
                <p className="whitespace-pre-wrap">{selected.condition_notes?.trim() || "Brak opisu stanu."}</p>
              </div>
              {selected.status === "rejected" && (
                <div>
                  <p className="text-xs text-muted-foreground">Powód odrzucenia</p>
                  <p className="whitespace-pre-wrap text-destructive">
                    {selected.rejection_reason?.trim() || "Brak powodu"}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Zdjęcia</p>
                <ProtocolPhotoGallery paths={selected.photo_urls ?? []} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
